import { useState } from 'react';

/**
 * @type {import('$types/components/hooks/filters.d.ts').ApplyFacetsToFilterOptions}
 */
const applyFacetsToFilterOptions = (options, value, facets) => {
    if (facets) {
        return options.map((option) => {
            if (option.value !== undefined) {
                const facet = facets?.find((f) => f.key === String(option.value));
                option.disabled = facets && !facet && !value?.includes(option.value);
            }
            return option;
        });
    }
    return options;
};

/* ========================================================================= */
/* Hook(s) */
/* ========================================================================= */

/**
 * @hook useMultiSelectFilter
 * @type {import('$types/components/hooks/filters.d.ts').UseMultiSelectFilter}
 */
const useMultiSelectFilter = ({
    initialValue = [],
    options = [],
    facets
}) => {
    const [_value, setValue] = useState(initialValue || []);
    const [_facets, setFacets] = useState(facets || []);

    /** @type {(key: string) => void} */
    const addValue = (key) => {
        setValue([..._value, key]);
    };

    const clear = () => {
        setValue([]);
    };

    /** @type {(key: string) => void} */
    const removeValue = (key) => {
        let values = [..._value];
        const index = values.indexOf(key);
        if (index > -1) { values.splice(index, 1); }
        setValue(values);
    };

    /** @type {(key: string) => void} */
    const toggleValue = (key) => {
        if (_value.indexOf(key) === -1) {
            addValue(key);
        } else {
            removeValue(key);
        }
    };

    return {
        addValue,
        clear,
        options: facets ? applyFacetsToFilterOptions(options, _value, _facets) : options,
        removeValue,
        setFacets,
        setValue,
        toggleValue,
        value: _value
    };
};

/**
 * @hook useRangeFilter
 * @type {import('$types/components/hooks/filters.d.ts').UseRangeFilter}
 */
const useRangeFilter = ({
    initialValue = [0, 0],
    range = [0, 1]
}) => {
    const _initialMin = initialValue ? Math.max(initialValue[0], range[0]) : 0;
    const _initialMax = initialValue ? Math.max(_initialMin, Math.min(initialValue[1], range[1])) : 0;
    const [_value, setValue] = useState([_initialMin, _initialMax]);
    const [_range, setRange] = useState(range);

    const clear = () => {
        setValue(range);
    };

    return {
        clear,
        range: _range,
        setRange,
        setValue,
        value: _value,
    };
};

/**
 * @hook useSingleSelectFilter
 * @type {import('$types/components/hooks/filters.d.ts').UseSingleSelectFilter}
 */
const useSingleSelectFilter = ({
    initialValue = null,
    options = [],
    facets
}) => {
    const [_value, setValue] = useState(initialValue);
    const [_facets, setFacets] = useState(facets || []);

    const clear = () => {
        setValue(null);
    };

    return {
        clear,
        options: facets ? applyFacetsToFilterOptions(options, [_value], _facets) : options,
        setFacets,
        setValue,
        value: _value
    };
};

/**
 * @hook useBooleanFilter
 * @type {import('$types/components/hooks/filters.d.ts').UseBooleanFilter}
 */
const useBooleanFilter = ({
    disabled = false,
    initialValue = false
}) => {
    const [_disabled, setDisabled] = useState(disabled);
    const [_value, setValue] = useState(initialValue);

    const clear = () => {
        setValue(false);
    };

    return {
        clear,
        disabled: _disabled,
        setDisabled,
        setValue,
        value: _value
    };
};

/**
 * @hook useGenericStateFilter
 * @type {import('$types/components/hooks/filters.d.ts').UseGenericStateFilter}
 */
const useGenericStateFilter = ({
    defaultValue = null,
    initialValue = null
}) => {
    const [_value, setValue] = useState(initialValue);

    const clear = () => {
        setValue(null);
    };

    return {
        clear,
        defaultValue,
        setValue,
        value: _value
    };
};

/* ========================================================================= */
/* Exports */
/* ========================================================================= */

export {
    useBooleanFilter,
    useMultiSelectFilter,
    useRangeFilter,
    useSingleSelectFilter,
    useGenericStateFilter,
};
